import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, renderSlot as _renderSlot, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, Fragment as _Fragment, normalizeStyle as _normalizeStyle, withModifiers as _withModifiers, Transition as _Transition, Teleport as _Teleport, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-2c2c29ca"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["id"]
const _hoisted_2 = { class: "endButtons" }
const _hoisted_3 = {
  key: 0,
  class: "progress"
}
const _hoisted_4 = {
  key: 4,
  class: "mdlFooter"
}
const _hoisted_5 = {
  key: 0,
  class: "leftAndRightContainer"
}
const _hoisted_6 = {
  key: 0,
  class: "footer-left"
}
const _hoisted_7 = {
  key: 1,
  class: "footer-right"
}

import {
  ComponentPublicInstance,
  computed,
  ComputedRef,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  Ref,
  ref,
} from 'vue';

import AppIdent from '@/components/AppIdent.vue';
import BrandIcon from '@/components/icons/BrandIcon.vue';
import LoadingAnimation from '@/components/LoadingAnimation.vue';
import ScrollableContent, { ScrollableContentApi } from '@/components/ScrollableContent.vue';
import BrowserDetection from '@/data/BrowserDetection';
import Tenant from '@/data/datatypes/Tenant';
import { trapFocus } from '@/data/helpers/DomHelper';
import { AppIdentDetails, MiniApp } from '@/data/tasks/MiniApp';
import { Routes } from '@/router';

// The duration in ms to wait before emitting the close event to allow the dialog to fade away
const fadeDuration = 500;

interface Props {
  id?: string;
  showCloseButton?: boolean;
  showEditButton?: boolean;
  showDeleteButton?: boolean;
  showBackButton?: boolean;
  width?: string;
  height?: string;
  minHeight?: string;
  headerClasses?: string;
  scrollable?: boolean;
  outsideClickClosable?: boolean;
  fade?: boolean;
  inProgress?: boolean;
  disablePortal?: boolean;
  unpaddedContent?: boolean;
  // Props required when showing the app ident:
  app?: MiniApp | AppIdentDetails;
  userTenantId?: string;
  tenants?: Record<string, Tenant>;
  emitOnlyOnClose?: boolean; // If this is true, clicking close will only emit the close event, not close the dialog
}
interface Emits {
  (e: 'open'): void,
  (e: 'close', event?: Event): void,
  (e: 'edit', event?: Event): void,
  (e: 'delete', event?: Event): void,
}
export interface ModalDialogApi extends ComponentPublicInstance {
  closeDialog: (event?: Event) => Event | undefined;
  scrollToBottom: () => void;
  removeFromRouteModalCleanups: () => void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ModalDialog',
  props: {
    id: {},
    showCloseButton: { type: Boolean, default: false },
    showEditButton: { type: Boolean, default: false },
    showDeleteButton: { type: Boolean, default: false },
    showBackButton: { type: Boolean, default: false },
    width: { default: '40rem' },
    height: { default: '20rem' },
    minHeight: {},
    headerClasses: { default: '' },
    scrollable: { type: Boolean, default: true },
    outsideClickClosable: { type: Boolean, default: true },
    fade: { type: Boolean, default: true },
    inProgress: { type: Boolean, default: false },
    disablePortal: { type: Boolean, default: false },
    unpaddedContent: { type: Boolean, default: false },
    app: {},
    userTenantId: {},
    tenants: {},
    emitOnlyOnClose: { type: Boolean, default: false }
  },
  emits: ["open", "close", "edit", "delete"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const dialog: Ref<HTMLElement | null> = ref(null);
const scrollableContent: Ref<ScrollableContentApi | null> = ref(null);

const mouseDownOnBackground: Ref<boolean> = ref(false);
const show: Ref<boolean> = ref(false);

const isMobile: ComputedRef<boolean> = computed(() => {
  return BrowserDetection.isMobile();
});

const isSafariIos: ComputedRef<boolean> = computed(() => {
  return BrowserDetection.isSafariIos() || BrowserDetection.isIosApp();
});

const modalStyle = computed(() => {
  return {
    width: props.width,
    height: props.height,
    minHeight: props.minHeight,
  };
});

function emitClose(event?: Event): Event | undefined {
  emit('close', event);
  return event;
}

function edit(event?: Event): Event | undefined {
  emit('edit', event);
  return event;
}

function deleteEntry(event?: Event): Event | undefined {
  emit('delete', event);
  return event;
}

function handleMouseDown(event: Event): void {
  // We handle mousedown and mouseup manually due to a Vue bug where doing a mousedown on the dialog itself
  // and then mouseup on the background (e.g. selecting some text in the dialog, where you happen to drag
  // the mouse off the edge of the dialog) would incorrectly trigger the background's click handler.
  if (dialog.value === event.target) {
    mouseDownOnBackground.value = true;
  }
}

function handleMouseUp(event: MouseEvent): void {
  if (props.outsideClickClosable && mouseDownOnBackground.value && dialog.value === event.target) {
    closeDialog(event);
  }
  mouseDownOnBackground.value = false;
}

function onkey(event: KeyboardEvent) {
  if (event.key === 'Escape' && Routes.isTopModal(closeDialog)) {
    event.stopPropagation();
    closeDialog(event);
  }

  if (event.key === 'Tab' && Routes.isTopModal(closeDialog)) {
    event.stopPropagation();
    trapFocus(dialog.value, event);
  }
}

function scrollToBottom(): void {
  if (scrollableContent.value) {
    scrollableContent.value?.scrollToBottom();
  }
}

function closeDialog(event?: Event): Event | undefined {
  if (props.emitOnlyOnClose) {
    emit('close');
    return;
  }
  show.value = false;
  setTimeout(() => {
    emitClose(event);
  }, props.fade ? fadeDuration : 0);
  return event;
}

function removeFromRouteModalCleanups(): void {
  Routes.popModal(closeDialog);
}

onBeforeMount(() => {
  Routes.pushModal(closeDialog);
});

onBeforeUnmount(() => {
  Routes.popModal(closeDialog);
  window.removeEventListener('keydown', onkey);
});

window.addEventListener('keydown', onkey);
nextTick(() => {
  show.value = true;
  setTimeout(() => {
    emit('open');
  }, props.fade ? fadeDuration : 0);
});

__expose({
  closeDialog,
  scrollToBottom,
  removeFromRouteModalCleanups,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, {
    to: "#portal-target",
    disabled: _ctx.disablePortal
  }, [
    _createVNode(_Transition, {
      name: _ctx.fade ? 'fade' : 'none',
      mode: "out-in"
    }, {
      default: _withCtx(() => [
        (show.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(["modalDialogWrapper", [{withFooter: !!_ctx.$slots['footer']}, {withIdent: !!_ctx.app}]]),
              onMousedown: _withModifiers(handleMouseDown, ["stop"]),
              onMouseup: handleMouseUp,
              ref_key: "dialog",
              ref: dialog
            }, [
              _createElementVNode("div", {
                class: _normalizeClass(['modalDialog', {isWeb: !isMobile.value}]),
                id: _ctx.id ? _ctx.id : false,
                style: _normalizeStyle(modalStyle.value)
              }, [
                (_ctx.app)
                  ? (_openBlock(), _createBlock(AppIdent, {
                      key: 0,
                      userTenantId: _ctx.userTenantId,
                      app: _ctx.app,
                      tenants: _ctx.tenants,
                      showAppName: true
                    }, null, 8, ["userTenantId", "app", "tenants"]))
                  : _createCommentVNode("", true),
                (_ctx.$slots['heading'] || _ctx.showCloseButton)
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 1,
                      class: _normalizeClass(["mdlHeading", [{withHeading: !!_ctx.$slots['heading']}, _ctx.headerClasses]])
                    }, [
                      (_ctx.showBackButton)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 0,
                            class: "backButton",
                            title: "Back",
                            onClick: closeDialog
                          }, [
                            _createVNode(BrandIcon, {
                              class: "icon",
                              name: "special/chevron-left"
                            })
                          ]))
                        : _createCommentVNode("", true),
                      _renderSlot(_ctx.$slots, "heading"),
                      _createElementVNode("div", _hoisted_2, [
                        _renderSlot(_ctx.$slots, "extraButtons"),
                        (_ctx.showEditButton)
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 0,
                              class: "endButton",
                              title: "Edit",
                              onClick: edit
                            }, [
                              _createVNode(BrandIcon, {
                                class: "icon",
                                name: "light/edit-light"
                              })
                            ]))
                          : _createCommentVNode("", true),
                        (_ctx.showDeleteButton)
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 1,
                              class: "endButton",
                              title: "Delete",
                              onClick: deleteEntry
                            }, [
                              _createVNode(BrandIcon, {
                                class: "icon",
                                name: "light/delete-light"
                              })
                            ]))
                          : _createCommentVNode("", true),
                        (_ctx.showCloseButton)
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 2,
                              class: "endButton",
                              title: "Close",
                              onClick: closeDialog
                            }, [
                              _createVNode(BrandIcon, {
                                class: "icon closeIcon",
                                name: "special/dismiss"
                              })
                            ]))
                          : _createCommentVNode("", true)
                      ])
                    ], 2))
                  : _createCommentVNode("", true),
                (_ctx.scrollable)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                      _createVNode(ScrollableContent, {
                        class: _normalizeClass(["mdlContent scrollable", { safariIos: isSafariIos.value, unpadded: _ctx.unpaddedContent }]),
                        ref_key: "scrollableContent",
                        ref: scrollableContent
                      }, {
                        default: _withCtx(() => [
                          _renderSlot(_ctx.$slots, "default")
                        ], undefined, true),
                        _: 3
                      }, 8, ["class"]),
                      (_ctx.inProgress)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                            _createVNode(LoadingAnimation, { class: "mdlContent loading" })
                          ]))
                        : _createCommentVNode("", true)
                    ], 64))
                  : (_openBlock(), _createElementBlock("div", {
                      key: 3,
                      class: _normalizeClass(["mdlContent", { unpadded: _ctx.unpaddedContent }])
                    }, [
                      _renderSlot(_ctx.$slots, "default")
                    ], 2)),
                (_ctx.$slots['footer'] || _ctx.$slots['footer-left'] || _ctx.$slots['footer-right'])
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      _renderSlot(_ctx.$slots, "footer"),
                      (_ctx.$slots['footer-left'] || _ctx.$slots['footer-right'])
                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                            (_ctx.$slots['footer-left'])
                              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                  _renderSlot(_ctx.$slots, "footer-left")
                                ]))
                              : _createCommentVNode("", true),
                            (_ctx.$slots['footer-right'])
                              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                  _renderSlot(_ctx.$slots, "footer-right")
                                ]))
                              : _createCommentVNode("", true)
                          ]))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true)
              ], 14, _hoisted_1)
            ], 34))
          : _createCommentVNode("", true)
      ], undefined, true),
      _: 3
    }, 8, ["name"])
  ], 8, ["disabled"]))
}
}

})